<script setup>
import { ref, onMounted, watch, defineProps } from "vue"
import { KNOLIA } from "@/knolia"
import { Gems } from "../gems"
import { MESSAGES } from "../messages"
import router from "../router"
import BaseSelect from "../components/BaseSelect.vue";

const props = defineProps({
  userStore: {
    type: Object,
    default: Object
  }
})

const defaultImage = ref(require('@/images/default.png'))
const activeTab = ref('functions-list')
const functionsListData = ref([])
const activitiesListData = ref([])
const participationLevel = ref([])
const showAll = ref(false)
const showAllJobs = ref(false)
const userData = ref({})
const selfPotential = ref(true)
const selectedParticipationLevel = ref(null)
const selectedParticipationDetail = ref(0)

const generateGradient = (percent, color) => {
  return {
    background: `conic-gradient(${color} 0% ${percent}%, #10133D ${percent}% 100%)`
  };
};

async function fetchLists() {
  const response = await KNOLIA.CallBackEnd("get", "challenges-base")

  if(response.error) {
    Gems.Telegram(MESSAGES.ERROR.DEFAULT, 'tele-not-allowed')
    return
  }

  functionsListData.value = response.data.functions_list
  activitiesListData.value = response.data.activities_list
  participationLevel.value = response.data.participation_level
}

function getParticipationProficiency(task) {
    let proficiency = task.proficiency ?? 0
    let percent = 0

    if(task.participation_detail == 1)
        percent = proficiency * 100 / task.participation_list[task.participation_level - 1].ideal 
    else
        percent = proficiency * 100 / task.participation_list[task.participation_level - 1].min 
    
    if(isFinite(percent)) {
        return isNaN(percent) ? 0 : percent 
    } else {
        return 100
    }

}

function checkKnowledgeRating(item) {
    const participationLevel = Number(item.participation_level)
    const minimumKnowledgeRating = Number(item.minimum_knowledge_rating ?? 0)
    item.knowledge_insufficient = participationLevel >= 4 && minimumKnowledgeRating < participationLevel
}

function getParticipationGap(task) {
    let percent = getParticipationProficiency(task)
    
    if(percent > 100)
        return 0
    else
        return Math.round((100 - percent) * 100) / 100
}

function getProficiencyProficiency(opSkill) {
    let proficiency = opSkill.proficiency ?? 0

    let percent = proficiency * 100 / opSkill.proficiency_list[opSkill.proficiency_level - 1].min 
    
    if(isFinite(percent)) {
        return isNaN(percent) ? 0 : percent 
    } else {
        return 100
    }
}

function getProficiencyGap(opSkill) {
    let percent = getProficiencyProficiency(opSkill)
    
    if(percent > 100)
        return 0
    else
        return Math.round((100 - percent) * 100) / 100
}

function filterActivities() {
    if(selectedParticipationLevel.value) {
        let minProficiency = participationLevel.value[selectedParticipationLevel.value - 1].min
        
        if(selectedParticipationDetail.value == 1)
            minProficiency = participationLevel.value[selectedParticipationLevel.value - 1].ideal

        activitiesListData.value.forEach(activity => {
            activity.showSelf = false
            activity.participation_level = selectedParticipationLevel.value
            activity.participation_detail = selectedParticipationDetail.value
            checkKnowledgeRating(activity)
    
            if(activity.proficiency >= minProficiency)
                activity.showSelf = true
        })
    } else {
        activitiesListData.value.forEach(activity => {
            activity.showSelf = true
            activity.participation_level = 3
            activity.participation_detail = 0
            checkKnowledgeRating(activity)
        })
    }
}

function filterJobActivities() {
    if(selectedParticipationLevel.value) {
        let minProficiency = participationLevel.value[selectedParticipationLevel.value - 1].min
        
        if(selectedParticipationDetail.value == 1)
            minProficiency = participationLevel.value[selectedParticipationLevel.value - 1].ideal
    
        functionsListData.value.forEach(job => {
            job.showSelf = false
            Object.values(job.children).forEach(activity => {
                activity.showSelf = false
                activity.participation_level = selectedParticipationLevel.value
                activity.participation_detail = selectedParticipationDetail.value
                checkKnowledgeRating(activity)
        
                if(activity.proficiency >= minProficiency){
                    activity.showSelf = true
                    job.showSelf = true
                }
            })
        })
    } else {
        functionsListData.value.forEach(job => {
            job.showSelf = true
            Object.values(job.children).forEach(activity => {
                activity.showSelf = true
                activity.participation_level = 3
                activity.participation_detail = 0
                checkKnowledgeRating(activity)
            })
        })
    }
}

watch(showAll, (e) => {
    activitiesListData.value.forEach(activity => {
        activity.showChildren = e ? true : false

        Object.values(activity.children).forEach(task => {
            task.showChildren = e ? true : false

            Object.values(task.children).forEach(skill => {
                skill.showChildren = e ? true : false
            })
        })
    })

})

watch(showAllJobs, (e) => {
    functionsListData.value.forEach(job => {
        job.showChildren = e ? true : false

        Object.values(job.children).forEach(activity => {
            activity.showChildren = e ? true : false

            Object.values(activity.children).forEach(task => {
                task.showChildren = e ? true : false
            })
        })
    })

})

onMounted(async () => {
    selfPotential.value = KNOLIA.GetRequestedUserId() == KNOLIA.GetLoggedUserId()
    if(!selfPotential.value) {
        const response = await KNOLIA.CallBackEnd('get', `get-user`)
        
        response.data.user.accept_validate = Boolean(response.data.user.accept_validate) ?? false
        userData.value = response.data.user
    } else {
        userData.value = props.userStore.User
    }

  fetchLists()
});
</script>

<template>
    <div>
        <div class="tab-container">
            <div class="tab" :class="{'tab-active': activeTab === 'functions-list'}" @click="activeTab = 'functions-list'; filterJobActivities()">
                <span>Funções</span>
            </div>
            <div class="tab" :class="{'tab-active': activeTab === 'activities-list'}" @click="activeTab = 'activities-list'; filterActivities()">
                <span>Atividades</span>
            </div>
        </div>

        <div class="page-container">

            <div id="div-user-header">
                <img class="img-user-photo" :src="userData.src ?? defaultImage" @error="$event.target.src = defaultImage">

                <div>
                    <div id="div-name" class="line-clamp line-clamp-1 fw-bold">{{ userData.complete_name }}</div>
                    
                    <div class="line-clamp line-clamp-1">Nº Interno: <strong>{{ userData.user_internal }}</strong></div>

                    <div id="div-teams">
                        Equipa(s): 
                        <span class="fw-bold" v-if="userData.assigned_teams?.length == 0">[sem equipa]</span>
                        <span class="fw-bold" v-else-if="userData.assigned_teams?.length == 1" v-for="team in userData.assigned_teams" :key="team">{{ team.designation }}</span>
                        <span class="fw-bold" v-else>{{ userData.assigned_teams?.length }} equipas</span>
                    </div>
                </div>

                <i class="fas fa-arrow-left go-back-icon cursor-pointer" @click="router.go(-1)" @mouseenter="Gems.Tooltip($event.target, 'Retroceder', 'tooltip')"></i>
            </div>

            <div id="div-separation-line"></div>

            <div v-if="activeTab === 'functions-list'">
                <div class="d-flex gap-2 w-25">
                    <div class="flex-grow-1">
                        <span>Nível de Atribuíção: </span>
                        <base-select class="ddl-alt-padding" v-model="selectedParticipationLevel" @change="filterJobActivities()">
                            <option value="">Escolha um nível de atribuíção...</option>
                            <option v-for="(participation, i) in participationLevel" :value="participation.level" :key="i">{{ participation.designation }}</option>
                        </base-select>
                    </div>

                    <div class="flex-grow-2" v-show="selectedParticipationLevel">
                        <span>Detalhe: </span>
                        <base-select class="sel-prof-level ddl-alt-padding" v-model="selectedParticipationDetail" @change="filterJobActivities()">
                            <option value="0">Min</option>
                            <option value="1">Padrão</option>
                        </base-select>
                    </div>
                </div>

                <div class="div-tab-title-container">
                    <div class="line-clamp line-clamp-1">Funções em potencial</div>
                    <div class="show-all-container cursor-pointer" @click="showAllJobs = !showAllJobs" v-if="functionsListData.length > 0">
                        <span>Expandir/Fechar tudo</span>
                        <i class="icon fas" :class="{ 'fa-plus-square': !showAllJobs, 'fa-minus-square': showAllJobs }" />
                    </div>
                </div>

                <div v-if="functionsListData.length == 0">
                    <div class="div-element div-job cursor-default justify-content-between">
                        <div class="line-clamp line-clamp-1" @mouseenter="Gems.TooltipIfNeeded($event.target)">
                            <span>Sem funções em potencial</span>
                        </div>
                    </div>
                </div>

                <div v-for="job in functionsListData" :key="job.id" v-show="job.showSelf">
                    <div class="div-element div-job cursor-default justify-content-between">
                        <div class="line-clamp line-clamp-1" @mouseenter="Gems.TooltipIfNeeded($event.target)">
                            <span @mouseenter="Gems.Tooltip($event.target, 'Função', 'tooltip')">{{ job.designation }}</span>
                        </div>
                        <i class="fas cursor-pointer skills" @click="job.showChildren = !job.showChildren"
                            :class="{ 'fa-chevron-down': !job.showChildren, 'fa-chevron-up': job.showChildren }"></i>
                    </div>
                    
                    <div class="div-op-skills-container" v-show="job.showChildren">
                        <div v-for="activity in job.children" :key="activity.id" v-show="activity.showSelf">
                            <div class="div-element div-activity cursor-default justify-content-between">
                                <div class="line-clamp line-clamp-1" @mouseenter="Gems.TooltipIfNeeded($event.target)">
                                    <span @mouseenter="Gems.Tooltip($event.target, 'Atividade', 'tooltip')">{{ activity.designation }}</span>
                                </div>

                                <div class="text-end without-knowledge">
                                    <span v-if="activity.knowledge_insufficient">Conhecimento insuficiente</span>
                                </div>
                                <span class="text-end" v-if="activity.children.length == 0">[Sem Competências Associadas]</span>

                                <div class="div-participation-container" v-else>
                                    <base-select class="sel-assignment-level ddl-inside-row-padding" v-model="activity.participation_level" @change="checkKnowledgeRating(activity)">
                                        <option v-for="assignment in activity.participation_list" :key="assignment.level" :value="assignment.level">{{ assignment.designation }}</option>
                                    </base-select>

                                    <base-select class="sel-prof-level ddl-inside-row-padding" v-model="activity.participation_detail">
                                        <option value="0">Min</option>
                                        <option value="1">Padrão</option>
                                    </base-select>

                                    <div :style="generateGradient(getParticipationProficiency(activity), '#3f739f')" class="pie-chart" 
                                        @mouseenter="Gems.Tooltip($event.target, `Em falta: ${getParticipationGap(activity)}%`, 'tooltip')"></div>
                                </div>

                                <i class="fas cursor-pointer skills" @click="activity.showChildren = !activity.showChildren"
                                    :class="{ 'fa-chevron-down': !activity.showChildren, 'fa-chevron-up': activity.showChildren }"></i>
                                
                            </div>

                            <div class="div-op-skills-container" v-show="activity.showChildren">
                                <div v-for="task in activity.children" :key="task.id">
                                    <div class="div-element div-task cursor-default justify-content-between" :class="{'without-proficiency': task.children.length == 0}">
                                        <div class="line-clamp line-clamp-1" @mouseenter="Gems.TooltipIfNeeded($event.target)">
                                            <span @mouseenter="Gems.Tooltip($event.target, 'Tarefa', 'tooltip')">{{ task.designation }}</span>
                                        </div>

                                        <div class="text-end without-knowledge">
                                            <span v-if="task.knowledge_insufficient">Conhecimento insuficiente</span>
                                        </div>
                                        <span class="text-end" v-if="task.children.length == 0">[Sem Competências Associadas]</span>
                                        
                                        <div class="div-participation-container" v-else>
                                            <base-select class="sel-assignment-level ddl-inside-row-padding" v-model="task.participation_level" @change="checkKnowledgeRating(task)">
                                                <option v-for="assignment in task.participation_list" :key="assignment.level" :value="assignment.level">{{ assignment.designation }}</option>
                                            </base-select>

                                            <base-select class="sel-prof-level ddl-inside-row-padding" v-model="task.participation_detail">
                                                <option value="0">Min</option>
                                                <option value="1">Padrão</option>
                                            </base-select>

                                            <div :style="generateGradient(getParticipationProficiency(task), '#3f739f')" class="pie-chart" 
                                                @mouseenter="Gems.Tooltip($event.target, `Em falta: ${getParticipationGap(task)}%`, 'tooltip')"></div>
                                        </div>
                                        <i class="fas cursor-pointer skills" @click="task.showChildren = !task.showChildren" v-if="Object.keys(task.children).length > 0"
                                           :class="{ 'fa-chevron-down': !task.showChildren, 'fa-chevron-up': task.showChildren }"></i>
                                    </div>

                                    <div class="div-op-skills-container" v-show="task.showChildren">
                                        <div v-for="opSkill in task.children" :key="opSkill.id">
                                            <div class="div-element div-op-skill cursor-default justify-content-between">
                                                <div class="line-clamp line-clamp-1" @mouseenter="Gems.TooltipIfNeeded($event.target)">
                                                    <span @mouseenter="Gems.Tooltip($event.target, 'Competência Operativa', 'tooltip')">{{ opSkill.designation }}</span>
                                                </div>

                                                <div class="div-proficiency-container">
                                                    <base-select class="sel-assignment-level ddl-inside-row-padding" v-model="opSkill.proficiency_level">
                                                        <option v-for="proficiency in opSkill.proficiency_list" :key="proficiency.id" :value="proficiency.id">{{ proficiency.designation }}</option>
                                                    </base-select>

                                                    <div :style="generateGradient(getProficiencyProficiency(opSkill), '#3f739f')" class="pie-chart" 
                                                        @mouseenter="Gems.Tooltip($event.target, `Em falta: ${getProficiencyGap(opSkill)}%`, 'tooltip')"></div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                

            </div>

            <div v-if="activeTab === 'activities-list'">
                <div class="d-flex gap-2 w-25">
                    <div class="flex-grow-1">
                        <span>Nível de Atribuíção: </span>
                        <base-select class="ddl-alt-padding" v-model="selectedParticipationLevel" @change="filterActivities()">
                            <option value="">Escolha um nível de atribuíção...</option>
                            <option v-for="(participation, i) in participationLevel" :value="participation.level" :key="i">{{ participation.designation }}</option>
                        </base-select>
                    </div>

                    <div class="flex-grow-2" v-show="selectedParticipationLevel">
                        <span>Detalhe: </span>
                        <base-select class="sel-prof-level ddl-alt-padding" v-model="selectedParticipationDetail" @change="filterActivities()">
                            <option value="0">Min</option>
                            <option value="1">Padrão</option>
                        </base-select>
                    </div>
                </div>

                <div class="div-tab-title-container">
                    <div class="line-clamp line-clamp-1">Atividades em potencial</div>
                    <div class="show-all-container cursor-pointer" @click="showAll = !showAll" v-if="activitiesListData.length > 0">
                        <span>Expandir/Fechar tudo</span>
                        <i class="icon fas" :class="{ 'fa-plus-square': !showAll, 'fa-minus-square': showAll }" />
                    </div>
                </div>

                <div v-if="activitiesListData.length == 0">
                    <div class="div-element div-job cursor-default justify-content-between">
                        <div class="line-clamp line-clamp-1" @mouseenter="Gems.TooltipIfNeeded($event.target)">
                            <span>Sem atividades em potencial</span>
                        </div>
                    </div>
                </div>

                <div v-for="activity in activitiesListData" :key="activity.id" v-show="activity.showSelf">
                    <div class="div-element div-activity cursor-default justify-content-between">
                        <div class="line-clamp line-clamp-1" @mouseenter="Gems.TooltipIfNeeded($event.target)">
                            <span @mouseenter="Gems.Tooltip($event.target, 'Atividade', 'tooltip')">{{ activity.designation }}</span>
                        </div>
                        
                        <div class="text-end without-knowledge">
                            <span v-if="activity.knowledge_insufficient">Conhecimento insuficiente</span>
                        </div>
                        <span class="text-end" v-if="activity.children.length == 0">[Sem Competências Associadas]</span>

                        <div class="div-participation-container" v-else>
                            <base-select class="sel-assignment-level ddl-inside-row-padding" v-model="activity.participation_level" @change="checkKnowledgeRating(activity)">
                                <option v-for="assignment in activity.participation_list" :key="assignment.level" :value="assignment.level">{{ assignment.designation }}</option>
                            </base-select>

                            <base-select class="sel-prof-level ddl-inside-row-padding" v-model="activity.participation_detail">
                                <option value="0">Min</option>
                                <option value="1">Padrão</option>
                            </base-select>

                            <div :style="generateGradient(getParticipationProficiency(activity), '#3f739f')" class="pie-chart" 
                                @mouseenter="Gems.Tooltip($event.target, `Em falta: ${getParticipationGap(activity)}%`, 'tooltip')"></div>
                        </div>
                        <i class="fas cursor-pointer skills" @click="activity.showChildren = !activity.showChildren"
                            :class="{ 'fa-chevron-down': !activity.showChildren, 'fa-chevron-up': activity.showChildren }"></i>
                        
                    </div>

                    <div class="div-op-skills-container" v-show="activity.showChildren">
                        <div v-for="task in activity.children" :key="task.id">
                            <div class="div-element div-task cursor-default justify-content-between" :class="{'without-proficiency': task.children.length == 0}">
                                <div class="line-clamp line-clamp-1" @mouseenter="Gems.TooltipIfNeeded($event.target)">
                                    <span @mouseenter="Gems.Tooltip($event.target, 'Tarefa', 'tooltip')">{{ task.designation }}</span>
                                </div>

                                <div class="text-end without-knowledge">
                                    <span v-if="task.knowledge_insufficient">Conhecimento insuficiente</span>
                                </div>
                                <span class="text-end" v-if="task.children.length == 0">[Sem Competências Associadas]</span>
                                
                                <div class="div-participation-container" v-else>
                                    <base-select class="sel-assignment-level ddl-inside-row-padding" v-model="task.participation_level" @change="checkKnowledgeRating(task)">
                                        <option v-for="assignment in task.participation_list" :key="assignment.level" :value="assignment.level">{{ assignment.designation }}</option>
                                    </base-select>

                                    <base-select class="sel-prof-level ddl-inside-row-padding" v-model="task.participation_detail">
                                        <option value="0">Min</option>
                                        <option value="1">Padrão</option>
                                    </base-select>

                                    <div :style="generateGradient(getParticipationProficiency(task), '#3f739f')" class="pie-chart" 
                                        @mouseenter="Gems.Tooltip($event.target, `Em falta: ${getParticipationGap(task)}%`, 'tooltip')"></div>
                                </div>
                                
                                <i class="fas cursor-pointer skills" @click="task.showChildren = !task.showChildren" v-if="Object.keys(task.children).length > 0"
                                    :class="{ 'fa-chevron-down': !task.showChildren, 'fa-chevron-up': task.showChildren }"></i>
                            </div>

                            <div class="div-op-skills-container" v-show="task.showChildren">
                                <div class="div-element div-op-skill cursor-default" v-for="opSkill in task.children" :key="opSkill.id">
                                    <div class="line-clamp line-clamp-1" @mouseenter="Gems.TooltipIfNeeded($event.target)">
                                        <span @mouseenter="Gems.Tooltip($event.target, 'Competência Operativa', 'tooltip')">{{ opSkill.designation }}</span>
                                    </div>
                                    <div class="div-proficiency-container">
                                        <base-select class="sel-assignment-level ddl-inside-row-padding" v-model="opSkill.proficiency_level">
                                            <option v-for="proficiency in opSkill.proficiency_list" :key="proficiency.id" :value="proficiency.id">{{ proficiency.designation }}</option>
                                        </base-select>

                                        <div :style="generateGradient(getProficiencyProficiency(opSkill), '#3f739f')" class="pie-chart" 
                                            @mouseenter="Gems.Tooltip($event.target, `Em falta: ${getProficiencyGap(opSkill)}%`, 'tooltip')"></div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped>

    /* USER DATA */
    #div-teams {
        display: flex;
        gap: 5px;
        cursor: default;
    }

    .div-team + * {
        padding-left: 10px;
        border-left: 1px solid black;
    }

    #div-user-content {
        background-color: white;
        padding: 20px;
        width: 50%;
        box-shadow: rgba(0, 0, 0, 0.35) 0px 5px 15px;
        border-radius: 5px;
    }

    #div-user-header {
        font-size: 1.3em;
        display: flex;
        align-items: center;
        gap: 1%;
        margin-bottom: 1%;
    }

    #div-separation-line {
        border-top: 1px solid;
        margin: 5px 0px;
    }

    #div-name {
        display: flex;
        justify-content: space-between;
    }

    .img-user-photo {
        width: 100px;
        height: 100px;
        border-radius: 50%;
        background-size: cover;
        background-position: center;
        object-fit: cover;
        margin: 15px;
    }

    .go-back-icon {
        font-size: 5rem;
        right: 0;
        position: absolute;
        margin-right: 40px;
    }

    /* ************** */
    
    
    .tab-container {
        display: flex;
        justify-content: space-between;
        border-bottom: 1px solid #06071452;
    }

    .tab {
        display: flex;
        justify-content: center;
        text-transform: uppercase;
    }

    .tab div:nth-child(3) {
        margin-left: 20px;
    }

    .div-element {
        padding: 5px 15px 5px 15px;
        margin-top: 2px;
        display: grid;
        gap: 5px;
        align-items: center;
    }

    .div-op-skills-container {
        padding-left: 20px;
    }

    .div-job {
        background-color: var(--default-app-alt-line-color);
        grid-template-columns: 98% 1%; 
        gap: 10px;
    }

    .div-activity {
        background-color: #3f739f;
        /* grid-template-columns: 40% 58% 1%;  */
        grid-template-columns: 57% 20% 21% 1%;

    }

    .div-task {
        background-color: #405f7b;
        /* grid-template-columns: 40% 58% 2%;  */
        /* grid-template-columns: 75% 10% 6% 5% 2%; */
        grid-template-columns: 57% 20% 21% 1%;
    }

    .div-participation-container {
        display: grid;
        grid-template-columns: 50% 30% 16%;
        gap: 2%;
    }

    .div-proficiency-container {
        display: grid;
        grid-template-columns: 77% 23%;
    }

    .div-skill {
        background-color: #2f4a63;
        grid-template-columns: 98% 2%; 
        gap: 10px;
    }

    .div-op-skill {
        /* grid-template-columns: 40% 58% 2%;  */
        grid-template-columns: 80% 19% 1%;
        gap: 0px;
    }

    .show-all-container {
        display: flex;
        align-items: center;
        gap: 10px;
        justify-content: end;
    }
    
    .div-tab-title-container {
        font-size: 1.2em;
        font-weight: bold;
        text-transform: uppercase;
        display: flex;
        justify-content: space-between;
        margin: 10px 0px;
    }

    .without-proficiency{
        opacity: .7;
    }

    .without-knowledge {
        color: orangered;
        font-weight: bold;
    }

    .pie-chart {
        width: 25px;
        height: 25px;
        border-radius: 50%;
        border: 1px solid;
        margin: auto;
    }

    @media screen and (max-width: 768px) {

        #div-separation-line {
            margin: 0px 2%;
        }

        #div-user-header {
            font-size: 1.1em;
            gap: 2%;
            margin-bottom: 0%;
            padding: 2%;        
        }

        .div-tab-title-container {
            padding: 2% 2% calc(2% - 1px) 2%;
        }

        .img-user-photo {
            width: 65px;
            height: 65px;
        }

        .tab-container {
            width: 100%;
        }
    }
</style>